// import node module libraries
import { Fragment, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Modal,
  Nav,
  Row,
  Table,
  Tab,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";

// import sub components
import CommonHeaderTabs from "../CommonHeaderTabs";

import { DashboardContext } from "context/Context";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "index";
import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

// import bootstrap icons
import {
  Activity,
  BarChart,
  CalculatorFill,
  Calendar4,
  CashStack,
  Cpu,
  Droplet,
  FuelPump,
  GraphUpArrow,
  InfoCircle,
  Lightning,
  Trophy,
  PeopleFill,
} from "react-bootstrap-icons";
import { Copy } from "react-feather";

const round2 = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 100) / 100;
};

const toNullableNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return "—";
  return `$${value.toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatInt = (value) => {
  if (!Number.isFinite(value)) return "—";
  return Math.round(value).toLocaleString("es-MX");
};

const formatPct = (value) => {
  if (!Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
};

const formatR2 = (value) => {
  if (!Number.isFinite(value)) return "—";
  return value.toFixed(3);
};

const formatDiff = (a, b) => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const diff = a - b;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff.toFixed(2)}`;
};

const diffColor = (a, b) => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "text-muted";
  const diff = a - b;
  if (Math.abs(diff) < 0.001) return "text-muted";
  return diff > 0 ? "text-danger" : "text-success";
};

// --- Result normalizers ---------------------------------------------------

const normalizePolyFuel = (fuelData, cost, suggestedFallback) => {
  if (!fuelData) return null;
  const perfect = fuelData.perfectPrice || {};
  const coeffs = Array.isArray(fuelData.regression) ? fuelData.regression : null;

  const suggestedPrice =
    round2(perfect.suggestedPrice) ?? round2(suggestedFallback) ?? null;

  let liters = null;
  if (Number.isFinite(perfect.topLitersSold)) liters = round2(perfect.topLitersSold);
  if (!Number.isFinite(liters) && coeffs && Number.isFinite(suggestedPrice)) {
    liters = round2(
      coeffs[0] * suggestedPrice * suggestedPrice +
        coeffs[1] * suggestedPrice +
        coeffs[2],
    );
  }
  liters = Number.isFinite(liters) ? Math.max(0, liters) : null;

  let profit = round2(perfect.lastProfit);
  if (!Number.isFinite(profit) && Number.isFinite(suggestedPrice) && Number.isFinite(liters)) {
    profit = round2(Math.max(0, (suggestedPrice - Number(cost || 0)) * liters));
  }

  return {
    suggestedPrice,
    expectedProfit: Number.isFinite(profit) ? profit : null,
    estimatedVolume: liters,
    margin: Number.isFinite(suggestedPrice) ? round2(suggestedPrice - Number(cost || 0)) : null,
    modelType: "Polinomial 2° (precio propio)",
    r2: toNullableNumber(perfect.r2),
    samples: toNullableNumber(perfect.samples),
    coefficients: coeffs,
    bounds: perfect.bounds || null,
    capped: !!perfect.capped,
    fallbackReasons: Array.isArray(perfect.fallbackReasons) ? perfect.fallbackReasons : [],
    ok: !!perfect.ok || Number.isFinite(suggestedPrice),
    error: perfect.error || null,
    raw: fuelData,
  };
};

const normalizeClaudeFuel = (fuelData, cost) => {
  if (!fuelData) return null;
  const perfect = fuelData.perfectPrice || {};
  const model = fuelData.model || {};

  const suggestedPrice = round2(perfect.suggestedPrice);
  const liters = Number.isFinite(perfect.topLitersSold) ? round2(perfect.topLitersSold) : null;

  let profit = round2(perfect.lastProfit);
  if (!Number.isFinite(profit) && Number.isFinite(suggestedPrice) && Number.isFinite(liters)) {
    profit = round2(Math.max(0, (suggestedPrice - Number(cost || 0)) * liters));
  }

  const isRivalAware = model.type === "rival_aware_quadratic";

  return {
    suggestedPrice,
    expectedProfit: Number.isFinite(profit) ? profit : null,
    estimatedVolume: Number.isFinite(liters) ? Math.max(0, liters) : null,
    margin: Number.isFinite(suggestedPrice) ? round2(suggestedPrice - Number(cost || 0)) : null,
    modelType: isRivalAware
      ? "Cuadrática multivariada (precio + rival)"
      : "Polinomial 2° (fallback sin rival)",
    isRivalAware,
    r2: toNullableNumber(perfect.r2 ?? model.r2),
    samples: toNullableNumber(model.samples),
    coefficients: isRivalAware
      ? model.effectiveCoefficients || null
      : model.coefficients || null,
    beta: isRivalAware ? model.beta || null : null,
    rivalAnchor: toNullableNumber(model.rivalAnchor),
    bounds: perfect.bounds || null,
    capped: !!perfect.capped,
    usedFallback: !!perfect.usedFallback,
    fallbackReason: perfect.fallbackReason || model.fallbackReason || null,
    fallbackReasons: Array.isArray(perfect.fallbackReasons) ? perfect.fallbackReasons : [],
    ok: !!perfect.ok || Number.isFinite(suggestedPrice),
    error: perfect.error || null,
    raw: fuelData,
  };
};

// --- Small presentational helpers ----------------------------------------

const KpiTile = ({ icon, label, value, sub, accent = "primary" }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body className="d-flex align-items-center">
      <div
        className={`d-flex align-items-center justify-content-center rounded-3 me-3 bg-light-${accent} text-${accent}`}
        style={{ width: 48, height: 48, flexShrink: 0 }}
      >
        {icon}
      </div>
      <div className="flex-grow-1">
        <small className="text-muted d-block">{label}</small>
        <h3 className="mb-0">{value}</h3>
        {sub ? <small className="text-muted">{sub}</small> : null}
      </div>
    </Card.Body>
  </Card>
);

const ModelBadge = ({ result }) => {
  if (!result) return <Badge bg="light" text="muted">Sin ejecutar</Badge>;
  if (!result.ok) return <Badge bg="danger">Error: {result.error || "n/a"}</Badge>;
  if (result.usedFallback) return <Badge bg="warning" text="dark">Fallback sin rival</Badge>;
  if (result.fallbackReasons && result.fallbackReasons.length)
    return <Badge bg="warning" text="dark">Regresión limitada</Badge>;
  return <Badge bg="success">OK</Badge>;
};

const ComparisonCell = ({ result, cost }) => {
  if (!result) {
    return (
      <div className="text-center text-muted py-4">
        <small>Sin corrida</small>
      </div>
    );
  }
  if (!result.ok) {
    return (
      <div className="text-center py-4">
        <Badge bg="danger" className="mb-2">Sin resultado</Badge>
        <div>
          <small className="text-muted">{result.error || "error"}</small>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="d-flex justify-content-between align-items-baseline">
        <h2 className="mb-0">{formatMoney(result.suggestedPrice)}</h2>
        <ModelBadge result={result} />
      </div>
      <small className="text-muted d-block">Precio sugerido</small>

      <Row className="mt-3 g-2">
        <Col xs={6}>
          <small className="text-muted d-block">Utilidad</small>
          <strong>{formatMoney(result.expectedProfit)}</strong>
        </Col>
        <Col xs={6}>
          <small className="text-muted d-block">Volumen est.</small>
          <strong>{formatInt(result.estimatedVolume)} L</strong>
        </Col>
        <Col xs={6}>
          <small className="text-muted d-block">Margen unitario</small>
          <strong>{formatMoney(result.margin)}</strong>
        </Col>
        <Col xs={6}>
          <small className="text-muted d-block">R² del modelo</small>
          <strong>{formatR2(result.r2)}</strong>
        </Col>
      </Row>
    </div>
  );
};

const RunDetailRows = ({ result }) => {
  if (!result) {
    return <p className="text-muted mb-0">Aún no se ha ejecutado este modelo.</p>;
  }
  const rows = [];
  rows.push(["Tipo de modelo", result.modelType || "—"]);
  rows.push(["Muestras usadas", result.samples != null ? `${result.samples} días` : "—"]);
  rows.push(["R²", formatR2(result.r2)]);
  if (result.isRivalAware != null) {
    rows.push(["Rival anchor", result.rivalAnchor != null ? formatMoney(result.rivalAnchor) : "—"]);
  }
  if (result.coefficients && result.coefficients.length === 3) {
    const [a, b, c] = result.coefficients;
    rows.push([
      "Coef. efectivos (a·p² + b·p + c)",
      `a=${Number(a).toExponential(2)}  b=${Number(b).toFixed(3)}  c=${Number(c).toFixed(2)}`,
    ]);
  }
  if (result.beta && result.beta.length === 5) {
    const labels = ["β₀", "β₁·p", "β₂·p²", "β₃·(p−r)", "β₄·(p−r)²"];
    rows.push([
      "Coef. multivariados",
      labels
        .map((lbl, i) => `${lbl}=${Number(result.beta[i]).toExponential(2)}`)
        .join("  "),
    ]);
  }
  if (result.bounds) {
    rows.push([
      "Bounds de precio",
      `${formatMoney(result.bounds.min)} → ${formatMoney(result.bounds.max)}`,
    ]);
  }
  rows.push(["Capped por bounds", result.capped ? "Sí" : "No"]);
  if (result.usedFallback) {
    rows.push(["Fallback activado", result.fallbackReason || "sí"]);
  }
  if (result.fallbackReasons && result.fallbackReasons.length) {
    rows.push(["Avisos de regresión", result.fallbackReasons.join(", ")]);
  }

  return (
    <Table size="sm" className="mb-0 align-middle">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td className="text-muted" style={{ width: "45%" }}>
              {k}
            </td>
            <td className="text-end fw-semi-bold" style={{ wordBreak: "break-word" }}>
              {v}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const RivalImpactsList = ({ impacts, accent }) => {
  if (!impacts || !impacts.length) {
    return (
      <p className="text-muted mb-0 small">
        No hay suficientes datos de rivales para medir impacto en este combustible.
      </p>
    );
  }
  const ranked = impacts.filter((i) => !i.skipped);
  const skipped = impacts.filter((i) => i.skipped);
  if (!ranked.length) {
    return (
      <p className="text-muted mb-0 small">
        No hay rival con datos suficientes (mínimo 6 días con precio y varianza).
      </p>
    );
  }
  const top = ranked.slice(0, 5);
  const maxAbs = Math.max(...top.map((r) => Math.abs(Number(r.coefRivalPrice) || 0)), 0.0001);
  return (
    <div>
      {top.map((r, idx) => {
        const coef = Number(r.coefRivalPrice);
        const rel = Number.isFinite(r.relativeImpactPct) ? r.relativeImpactPct * 100 : null;
        const widthPct = (Math.abs(coef) / maxAbs) * 100;
        const positive = coef > 0;
        const colorVariant = positive ? "success" : "danger";
        return (
          <div key={r.rivalId} className="mb-3">
            <div className="d-flex justify-content-between align-items-baseline">
              <div>
                {idx === 0 ? (
                  <Trophy className={`me-1 text-${accent}`} size={14} />
                ) : (
                  <span className="text-muted me-1">#{idx + 1}</span>
                )}
                <strong>{r.rivalName}</strong>
                <small className="text-muted ms-2">{r.samples} días · R² {formatR2(r.r2)}</small>
              </div>
              <div className="text-end">
                <span className={`fw-bold text-${colorVariant}`}>
                  {positive ? "+" : ""}
                  {coef.toFixed(2)} L
                </span>
                <small className="text-muted"> /$ rival</small>
                {rel != null ? (
                  <small className={`d-block text-${colorVariant}`}>
                    {positive ? "+" : ""}
                    {rel.toFixed(1)}% vs venta promedio
                  </small>
                ) : null}
              </div>
            </div>
            <div className="progress mt-1" style={{ height: 6 }}>
              <div
                className={`progress-bar bg-${colorVariant}`}
                role="progressbar"
                style={{ width: `${widthPct}%` }}
                aria-valuenow={widthPct}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>
        );
      })}
      {skipped.length ? (
        <small className="text-muted d-block mt-2">
          {skipped.length} rival{skipped.length === 1 ? "" : "es"} sin datos suficientes.
        </small>
      ) : null}
    </div>
  );
};

// --- Main component -------------------------------------------------------

const SalesForecast = () => {
  const [showModal, setShowModal] = useState(false);
  const [businessLocations, setBusinessLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({});
  const [loading, setLoading] = useState(false);
  const [runningMethod, setRunningMethod] = useState(null); // "poly" | "claude" | null

  // Results
  const [polyResult, setPolyResult] = useState(null); // { magna, premium, ranAt }
  const [claudeResult, setClaudeResult] = useState(null); // { magna, premium, meta, ranAt }

  // Rivals snapshot card (current per-rival prices, not the historical avg)
  const [rivalPriceStats, setRivalPriceStats] = useState({
    magna: { min: null, max: null, avg: null, minName: null, maxName: null },
    premium: { min: null, max: null, avg: null, minName: null, maxName: null },
  });
  const [loadingRivals, setLoadingRivals] = useState(false);

  // Date defaults
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const fourMonthsBack = new Date(now.getFullYear(), now.getMonth() - 4, 1);
    return new Date(fourMonthsBack.getFullYear(), fourMonthsBack.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };
  const getToday = () => new Date().toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [magnaCost, setMagnaCost] = useState(0);
  const [premiumCost, setPremiumCost] = useState(0);

  // Which method's number to highlight in the KPI strip
  const [activeMethod, setActiveMethod] = useState("claude"); // "claude" | "poly"

  const calculatePricesWithAI = httpsCallable(functions, "calculatePricesClaude");
  const calculatePolynomial = httpsCallable(functions, "calculatePrices4");

  const db = getFirestore();
  const { user } = useContext(DashboardContext);

  // The forwardRef is important for Dropdown positioning
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to=""
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn-icon btn btn-ghost btn-sm rounded-circle"
    >
      {children}
    </Link>
  ));

  const getLocs = async () => {
    const resp = [];
    const locsRef = collection(db, "companies", user.companyId, "locations");
    const locSnap = await getDocs(locsRef);
    locSnap.forEach((e) => resp.push(e.data()));
    if (resp.length >= 1) setBusinessLocations(resp);
  };

  const buildRivalStats = (entries = []) => {
    const valid = entries
      .map((e) => ({ price: Number(e.price), name: e.name }))
      .filter((e) => Number.isFinite(e.price));
    if (!valid.length) return { min: null, max: null, avg: null, minName: null, maxName: null };
    const minE = valid.reduce((p, c) => (c.price < p.price ? c : p));
    const maxE = valid.reduce((p, c) => (c.price > p.price ? c : p));
    const avg = valid.reduce((s, e) => s + e.price, 0) / valid.length;
    return {
      min: round2(minE.price),
      max: round2(maxE.price),
      avg: round2(avg),
      minName: minE.name || "Sin nombre",
      maxName: maxE.name || "Sin nombre",
    };
  };

  const fetchRivalPrices = async () => {
    if (!user?.companyId || !selectedLocation?.value?.locId) return;
    setLoadingRivals(true);
    try {
      const rivalsRef = collection(db, "companies", user.companyId, "rivals");
      const rivalsQuery = query(
        rivalsRef,
        where("locations", "array-contains", selectedLocation.value.locId),
      );
      const rivSnap = await getDocs(rivalsQuery);
      const magnaEntries = [];
      const premiumEntries = [];
      rivSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const rivalName = data.alias || data.name || "Sin nombre";
        const m = Number(data.magnaPrice);
        const p = Number(data.premiumPrice);
        if (Number.isFinite(m)) magnaEntries.push({ price: m, name: rivalName });
        if (Number.isFinite(p)) premiumEntries.push({ price: p, name: rivalName });
      });
      setRivalPriceStats({
        magna: buildRivalStats(magnaEntries),
        premium: buildRivalStats(premiumEntries),
      });
    } catch (error) {
      console.error("Error fetching rival prices:", error);
      toast.error("No se pudieron obtener los precios de los rivales");
    } finally {
      setLoadingRivals(false);
    }
  };

  const handleCalculate = async (method) => {
    if (
      !startDate ||
      !endDate ||
      !selectedLocation?.value?.locId ||
      !magnaCost ||
      !premiumCost ||
      !user?.companyId
    ) {
      toast.error("Por favor, llena todos los campos");
      return;
    }

    setLoading(true);
    setRunningMethod(method);

    const payload = {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      locationId: selectedLocation.value.locId,
      costMagna: Number(magnaCost),
      costPremium: Number(premiumCost),
      companyId: user.companyId,
    };

    try {
      if (method === "poly") {
        const res = await calculatePolynomial(payload);
        const data = res.data || {};
        setPolyResult({
          magna: normalizePolyFuel(data.magna, magnaCost),
          premium: normalizePolyFuel(data.premium, premiumCost),
          ranAt: new Date(),
        });
        setActiveMethod("poly");
        toast.success("Cálculo (ventas propias) listo");
      } else if (method === "claude") {
        const res = await calculatePricesWithAI(payload);
        const data = res.data || {};
        setClaudeResult({
          magna: normalizeClaudeFuel(data.magna, magnaCost),
          premium: normalizeClaudeFuel(data.premium, premiumCost),
          meta: data.meta || {},
          ranAt: new Date(),
        });
        setActiveMethod("claude");
        toast.success("Cálculo (ventas + rivales) listo");
      }
      await fetchRivalPrices();
      setShowModal(false);
    } catch (error) {
      console.error("Error during calculation:", error);
      toast.error("Error al calcular precios. Aumenta el rango de fechas para más datos.");
    } finally {
      setLoading(false);
      setRunningMethod(null);
    }
  };

  useEffect(() => {
    getLocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active values for the KPI strip
  const active = activeMethod === "poly" ? polyResult : claudeResult;
  const activeLabel =
    activeMethod === "poly" ? "Ventas propias (v4)" : "Ventas + rivales (Claude)";

  const meta = claudeResult?.meta || {};
  const rivalAnchorMagna =
    claudeResult?.magna?.rivalAnchor ?? toNullableNumber(meta.rivalLatestMagna);
  const rivalAnchorPremium =
    claudeResult?.premium?.rivalAnchor ?? toNullableNumber(meta.rivalLatestPremium);

  const handleCopy = () => {
    if (!active) {
      toast.error("Ejecuta un cálculo primero");
      return;
    }
    const lines = [
      `Estación: ${selectedLocation?.value?.name || "—"}`,
      `Modelo: ${activeLabel}`,
      `Periodo: ${startDate} → ${endDate}`,
      "",
      `Regular`,
      `  Precio sugerido: ${formatMoney(active.magna?.suggestedPrice)}`,
      `  Utilidad esperada: ${formatMoney(active.magna?.expectedProfit)}`,
      `  Volumen estimado: ${formatInt(active.magna?.estimatedVolume)} L`,
      "",
      `Premium`,
      `  Precio sugerido: ${formatMoney(active.premium?.suggestedPrice)}`,
      `  Utilidad esperada: ${formatMoney(active.premium?.expectedProfit)}`,
      `  Volumen estimado: ${formatInt(active.premium?.estimatedVolume)} L`,
    ].join("\n");
    navigator.clipboard.writeText(lines);
    toast.success("Copiado al portapapeles");
  };

  const ActionMenu = () => (
    <Dropdown>
      <Dropdown.Toggle as={CustomToggle}>
        <i className="fe fe-more-vertical text-muted"></i>
      </Dropdown.Toggle>
      <Dropdown.Menu align="end">
        <Dropdown.Item
          onClick={(e) => {
            e.preventDefault();
            getLocs().then(() => setShowModal(true));
          }}
        >
          <CalculatorFill className="me-2" size={16} />
          Nuevo Cálculo
        </Dropdown.Item>
        <Dropdown.Item
          onClick={(e) => {
            e.preventDefault();
            handleCopy();
          }}
        >
          <Copy size={16} className="me-2" />
          Copiar resultado activo
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const totalExpectedProfit = useMemo(() => {
    if (!active) return null;
    const m = Number(active.magna?.expectedProfit);
    const p = Number(active.premium?.expectedProfit);
    if (!Number.isFinite(m) && !Number.isFinite(p)) return null;
    return (Number.isFinite(m) ? m : 0) + (Number.isFinite(p) ? p : 0);
  }, [active]);

  const totalVolume = useMemo(() => {
    if (!active) return null;
    const m = Number(active.magna?.estimatedVolume);
    const p = Number(active.premium?.estimatedVolume);
    if (!Number.isFinite(m) && !Number.isFinite(p)) return null;
    return (Number.isFinite(m) ? m : 0) + (Number.isFinite(p) ? p : 0);
  }, [active]);

  const renderRivalsCard = (label, suggestedPrice, stats, accent) => {
    const hasOur = Number.isFinite(suggestedPrice) && suggestedPrice > 0;
    const hasRivals = stats && [stats.min, stats.avg, stats.max].some((v) => Number.isFinite(v));
    return (
      <Card className="h-100 border-0 shadow-sm">
        <Card.Header className="bg-white border-0 pb-0">
          <div className="d-flex align-items-center gap-2">
            <FuelPump className={`text-${accent}`} />
            <h5 className={`mb-0 text-${accent}`}>{label}</h5>
          </div>
        </Card.Header>
        <Card.Body>
          {!hasOur ? (
            <p className="text-muted mb-0">Ejecuta un cálculo para comparar.</p>
          ) : !hasRivals ? (
            <p className="text-muted mb-0">Aún no hay precios de rivales para esta estación.</p>
          ) : (
            <Table size="sm" borderless className="mb-0 align-middle">
              <tbody>
                <tr>
                  <td className="text-muted">Tu precio sugerido</td>
                  <td className="text-end fw-bold">{formatMoney(suggestedPrice)}</td>
                  <td className="text-end text-muted small">—</td>
                </tr>
                <tr>
                  <td>
                    Rival mínimo
                    {stats.minName ? (
                      <div className="text-muted small">{stats.minName}</div>
                    ) : null}
                  </td>
                  <td className="text-end fw-semi-bold">{formatMoney(stats.min)}</td>
                  <td className={`text-end ${diffColor(suggestedPrice, stats.min)} small`}>
                    {formatDiff(suggestedPrice, stats.min)}
                  </td>
                </tr>
                <tr>
                  <td>Rival promedio</td>
                  <td className="text-end fw-semi-bold">{formatMoney(stats.avg)}</td>
                  <td className={`text-end ${diffColor(suggestedPrice, stats.avg)} small`}>
                    {formatDiff(suggestedPrice, stats.avg)}
                  </td>
                </tr>
                <tr>
                  <td>
                    Rival máximo
                    {stats.maxName ? (
                      <div className="text-muted small">{stats.maxName}</div>
                    ) : null}
                  </td>
                  <td className="text-end fw-semi-bold">{formatMoney(stats.max)}</td>
                  <td className={`text-end ${diffColor(suggestedPrice, stats.max)} small`}>
                    {formatDiff(suggestedPrice, stats.max)}
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Fragment>
      {/* Modal: New calculation */}
      <Modal centered show={showModal} size="lg" onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Cálculo</Modal.Title>
        </Modal.Header>
        {loading ? (
          <Modal.Body>
            <div className="d-flex flex-column align-items-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="text-muted mt-2">
                Corriendo modelo: {runningMethod === "claude" ? "Ventas + rivales" : "Ventas propias"}
              </small>
            </div>
          </Modal.Body>
        ) : (
          <Modal.Body>
            <Row>
              <Col md={4} className="mb-3">
                <h5>Fecha inicial</h5>
                <input
                  type="date"
                  value={startDate}
                  className="form-control"
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col md={4} className="mb-3">
                <h5>Fecha final</h5>
                <input
                  type="date"
                  value={endDate}
                  className="form-control"
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col md={4} className="mb-3">
                <h5>Estación</h5>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable={false}
                  defaultValue={selectedLocation}
                  placeholder="Selecciona"
                  value={selectedLocation}
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "40px",
                      minHeight: "40px",
                      minWidth: "15rem",
                      borderRadius: "10px",
                      borderColor: "#ced4da",
                    }),
                  }}
                  options={businessLocations.map((e) => ({ value: e, label: e.name }))}
                  onChange={(e) => {
                    const prevId = selectedLocation?.value?.locId;
                    const nextId = e?.value?.locId;
                    if (prevId && nextId && prevId !== nextId) {
                      setPolyResult(null);
                      setClaudeResult(null);
                      setRivalPriceStats({
                        magna: { min: null, max: null, avg: null, minName: null, maxName: null },
                        premium: {
                          min: null,
                          max: null,
                          avg: null,
                          minName: null,
                          maxName: null,
                        },
                      });
                      setActiveMethod("claude");
                      toast.info("Estación cambiada: resultados anteriores descartados");
                    }
                    setSelectedLocation(e);
                  }}
                />
              </Col>
              <Col md={6} className="mb-3">
                <label htmlFor="magnaCost" className="form-label text-success fw-bold">
                  Costo de Gasolina Regular
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="magnaCost"
                  value={magnaCost}
                  onChange={(e) => setMagnaCost(e.target.value)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <label htmlFor="premiumCost" className="form-label text-danger fw-bold">
                  Costo de Gasolina Premium
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="premiumCost"
                  value={premiumCost}
                  onChange={(e) => setPremiumCost(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="outline-primary"
            disabled={loading}
            onClick={() => handleCalculate("poly")}
          >
            <BarChart className="me-2" /> Solo ventas propias
          </Button>
          <Button
            variant="primary"
            disabled={loading}
            onClick={() => handleCalculate("claude")}
          >
            <Cpu className="me-2" /> Ventas + rivales
          </Button>
        </Modal.Footer>
      </Modal>

      <CommonHeaderTabs />

      {/* Context header */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h2 className="mb-0">
                  {selectedLocation?.value?.name || "Forecasts de Precio"}
                </h2>
                {selectedLocation?.value?.name ? (
                  <Badge bg="light" text="dark" className="ms-2">
                    <Activity className="me-1" size={12} /> Modelo activo: {activeLabel}
                  </Badge>
                ) : null}
              </div>
              <small className="text-muted">
                {selectedLocation?.value?.fullAddress || "Selecciona una estación para iniciar"}
              </small>
              <div className="mt-3 d-flex flex-wrap gap-2">
                <Badge bg="light" text="dark" className="px-3 py-2">
                  <Calendar4 className="me-2" size={14} />
                  {startDate} → {endDate}
                </Badge>
                <Badge bg="light" text="dark" className="px-3 py-2">
                  <CashStack className="me-2 text-success" size={14} />
                  Costo Regular: {formatMoney(Number(magnaCost))}
                </Badge>
                <Badge bg="light" text="dark" className="px-3 py-2">
                  <CashStack className="me-2 text-danger" size={14} />
                  Costo Premium: {formatMoney(Number(premiumCost))}
                </Badge>
                {meta && meta.rivalCount != null ? (
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <PeopleFill className="me-2 text-info" size={14} />
                    Rivales en muestra: {meta.rivalCount}
                  </Badge>
                ) : null}
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <div className="d-flex justify-content-md-end gap-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    getLocs().then(() => setShowModal(true));
                  }}
                >
                  <CalculatorFill className="me-2" /> Nuevo cálculo
                </Button>
                <ActionMenu />
              </div>
              {(polyResult || claudeResult) && (
                <div className="mt-2 d-flex justify-content-md-end gap-1">
                  <Button
                    size="sm"
                    variant={activeMethod === "poly" ? "primary" : "outline-secondary"}
                    onClick={() => polyResult && setActiveMethod("poly")}
                    disabled={!polyResult}
                  >
                    v4
                  </Button>
                  <Button
                    size="sm"
                    variant={activeMethod === "claude" ? "primary" : "outline-secondary"}
                    onClick={() => claudeResult && setActiveMethod("claude")}
                    disabled={!claudeResult}
                  >
                    Claude
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI strip */}
      <Row className="g-3 mb-3">
        <Col md={6} lg={3}>
          <KpiTile
            icon={<GraphUpArrow size={22} />}
            label="Precio sugerido Regular"
            value={formatMoney(active?.magna?.suggestedPrice)}
            sub={
              Number.isFinite(active?.magna?.margin)
                ? `Margen ${formatMoney(active.magna.margin)}/L`
                : null
            }
            accent="success"
          />
        </Col>
        <Col md={6} lg={3}>
          <KpiTile
            icon={<GraphUpArrow size={22} />}
            label="Precio sugerido Premium"
            value={formatMoney(active?.premium?.suggestedPrice)}
            sub={
              Number.isFinite(active?.premium?.margin)
                ? `Margen ${formatMoney(active.premium.margin)}/L`
                : null
            }
            accent="danger"
          />
        </Col>
        <Col md={6} lg={3}>
          <KpiTile
            icon={<CashStack size={22} />}
            label="Utilidad diaria estimada"
            value={formatMoney(totalExpectedProfit)}
            sub="Suma de ambos productos"
            accent="primary"
          />
        </Col>
        <Col md={6} lg={3}>
          <KpiTile
            icon={<Droplet size={22} />}
            label="Volumen diario estimado"
            value={`${formatInt(totalVolume)} L`}
            sub="Suma de ambos productos"
            accent="info"
          />
        </Col>
      </Row>

      {/* Side-by-side method comparison */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">
                <Lightning className="me-2 text-warning" />
                Comparativa de modelos
              </h4>
              <small className="text-muted">
                Solo ventas propias (calculatePrices4) vs Ventas + rivales (calculatePricesClaude)
              </small>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {!polyResult && !claudeResult ? (
            <div className="text-center py-5 text-muted">
              <InfoCircle size={28} className="mb-2" />
              <div>Aún no hay corridas. Pulsa "Nuevo cálculo" para comenzar.</div>
            </div>
          ) : (
            <Row className="g-3">
              {/* Regular */}
              <Col lg={6}>
                <Card className="h-100 border">
                  <Card.Header className="bg-light-success border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 text-success">
                        <FuelPump className="me-2" /> Regular
                      </h5>
                      <small className="text-muted">
                        Costo: {formatMoney(Number(magnaCost))}
                      </small>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={6}>
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block mb-1">
                            <BarChart className="me-1" /> Ventas propias (v4)
                          </small>
                          <ComparisonCell result={polyResult?.magna} cost={magnaCost} />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="border rounded-3 p-3 h-100 bg-light-primary">
                          <small className="text-primary d-block mb-1 fw-bold">
                            <Cpu className="me-1" /> Ventas + rivales
                          </small>
                          <ComparisonCell result={claudeResult?.magna} cost={magnaCost} />
                        </div>
                      </Col>
                    </Row>
                    {polyResult?.magna?.ok && claudeResult?.magna?.ok ? (
                      <div className="mt-3 p-2 bg-light rounded">
                        <small className="text-muted">
                          Δ Claude vs v4:&nbsp;
                          <span
                            className={diffColor(
                              claudeResult.magna.suggestedPrice,
                              polyResult.magna.suggestedPrice,
                            )}
                          >
                            {formatDiff(
                              claudeResult.magna.suggestedPrice,
                              polyResult.magna.suggestedPrice,
                            )}{" "}
                            /L
                          </span>
                          {Number.isFinite(rivalAnchorMagna) ? (
                            <>
                              &nbsp;·&nbsp;Rival ancla:{" "}
                              <strong>{formatMoney(rivalAnchorMagna)}</strong>
                            </>
                          ) : null}
                        </small>
                      </div>
                    ) : null}
                  </Card.Body>
                </Card>
              </Col>
              {/* Premium */}
              <Col lg={6}>
                <Card className="h-100 border">
                  <Card.Header className="bg-light-danger border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 text-danger">
                        <FuelPump className="me-2" /> Premium
                      </h5>
                      <small className="text-muted">
                        Costo: {formatMoney(Number(premiumCost))}
                      </small>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={6}>
                        <div className="border rounded-3 p-3 h-100">
                          <small className="text-muted d-block mb-1">
                            <BarChart className="me-1" /> Ventas propias (v4)
                          </small>
                          <ComparisonCell result={polyResult?.premium} cost={premiumCost} />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="border rounded-3 p-3 h-100 bg-light-primary">
                          <small className="text-primary d-block mb-1 fw-bold">
                            <Cpu className="me-1" /> Ventas + rivales
                          </small>
                          <ComparisonCell result={claudeResult?.premium} cost={premiumCost} />
                        </div>
                      </Col>
                    </Row>
                    {polyResult?.premium?.ok && claudeResult?.premium?.ok ? (
                      <div className="mt-3 p-2 bg-light rounded">
                        <small className="text-muted">
                          Δ Claude vs v4:&nbsp;
                          <span
                            className={diffColor(
                              claudeResult.premium.suggestedPrice,
                              polyResult.premium.suggestedPrice,
                            )}
                          >
                            {formatDiff(
                              claudeResult.premium.suggestedPrice,
                              polyResult.premium.suggestedPrice,
                            )}{" "}
                            /L
                          </span>
                          {Number.isFinite(rivalAnchorPremium) ? (
                            <>
                              &nbsp;·&nbsp;Rival ancla:{" "}
                              <strong>{formatMoney(rivalAnchorPremium)}</strong>
                            </>
                          ) : null}
                        </small>
                      </div>
                    ) : null}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Run diagnostics */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white border-bottom">
          <h4 className="mb-0">
            <InfoCircle className="me-2 text-primary" />
            Diagnóstico de corridas
          </h4>
          <small className="text-muted">Datos detallados de cada modelo</small>
        </Card.Header>
        <Card.Body>
          <Tab.Container defaultActiveKey="claude">
            <Nav variant="pills" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="claude">
                  <Cpu className="me-2" /> Ventas + rivales (Claude)
                  {claudeResult ? (
                    <Badge bg="primary" pill className="ms-2">
                      {claudeResult.ranAt.toLocaleTimeString("es-MX", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Badge>
                  ) : null}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="poly">
                  <BarChart className="me-2" /> Ventas propias (v4)
                  {polyResult ? (
                    <Badge bg="primary" pill className="ms-2">
                      {polyResult.ranAt.toLocaleTimeString("es-MX", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Badge>
                  ) : null}
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="claude">
                {!claudeResult ? (
                  <p className="text-muted mb-0">
                    Aún no se ha ejecutado el modelo Claude. Pulsa "Nuevo cálculo" → "Ventas +
                    rivales".
                  </p>
                ) : (
                  <Row className="g-3">
                    <Col md={6}>
                      <h6 className="text-success">
                        <FuelPump className="me-2" />
                        Regular
                      </h6>
                      <RunDetailRows result={claudeResult.magna} />
                    </Col>
                    <Col md={6}>
                      <h6 className="text-danger">
                        <FuelPump className="me-2" />
                        Premium
                      </h6>
                      <RunDetailRows result={claudeResult.premium} />
                    </Col>
                    <Col xs={12}>
                      <div className="bg-light rounded p-3">
                        <small className="text-muted d-block fw-bold mb-2">
                          Meta de la corrida
                        </small>
                        <Row>
                          <Col md={3}>
                            <small className="text-muted d-block">Rivales en muestra</small>
                            <strong>{meta.rivalCount ?? "—"}</strong>
                          </Col>
                          <Col md={3}>
                            <small className="text-muted d-block">Días con rival Regular</small>
                            <strong>{meta.rivalDaysMagna ?? "—"}</strong>
                          </Col>
                          <Col md={3}>
                            <small className="text-muted d-block">Días con rival Premium</small>
                            <strong>{meta.rivalDaysPremium ?? "—"}</strong>
                          </Col>
                          <Col md={3}>
                            <small className="text-muted d-block">Ejecutado</small>
                            <strong>
                              {claudeResult.ranAt.toLocaleString("es-MX")}
                            </strong>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                )}
              </Tab.Pane>
              <Tab.Pane eventKey="poly">
                {!polyResult ? (
                  <p className="text-muted mb-0">
                    Aún no se ha ejecutado el modelo v4. Pulsa "Nuevo cálculo" → "Solo ventas
                    propias".
                  </p>
                ) : (
                  <Row className="g-3">
                    <Col md={6}>
                      <h6 className="text-success">
                        <FuelPump className="me-2" />
                        Regular
                      </h6>
                      <RunDetailRows result={polyResult.magna} />
                    </Col>
                    <Col md={6}>
                      <h6 className="text-danger">
                        <FuelPump className="me-2" />
                        Premium
                      </h6>
                      <RunDetailRows result={polyResult.premium} />
                    </Col>
                    <Col xs={12}>
                      <div className="bg-light rounded p-3">
                        <small className="text-muted d-block">
                          Ejecutado: <strong>{polyResult.ranAt.toLocaleString("es-MX")}</strong>
                        </small>
                      </div>
                    </Col>
                  </Row>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      {/* Rival impact ranking */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white border-bottom">
          <h4 className="mb-0">
            <Trophy className="me-2 text-warning" />
            Rivales más influyentes en tus ventas
          </h4>
          <small className="text-muted">
            Regresión por rival: <code>litros = β₀ + β₁·precio_propio + β₂·precio_rival</code>.
            β₂ &gt; 0 (verde) significa que al subir el rival, tú vendes más (sustitución). β₂ &lt; 0
            (rojo) indica comovimiento. Solo aparece después de correr el modelo Claude.
          </small>
        </Card.Header>
        <Card.Body>
          {!claudeResult ? (
            <p className="text-muted mb-0">
              Corre "Ventas + rivales" para ver el ranking de rivales por impacto.
            </p>
          ) : (
            <Row className="g-3">
              <Col lg={6}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FuelPump className="text-success" />
                  <h5 className="mb-0 text-success">Regular</h5>
                </div>
                <RivalImpactsList
                  impacts={claudeResult.meta?.rivalImpacts?.magna}
                  accent="success"
                />
              </Col>
              <Col lg={6}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FuelPump className="text-danger" />
                  <h5 className="mb-0 text-danger">Premium</h5>
                </div>
                <RivalImpactsList
                  impacts={claudeResult.meta?.rivalImpacts?.premium}
                  accent="danger"
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Rivals comparison */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">
                <PeopleFill className="me-2 text-info" />
                Comparativo con rivales (snapshot actual)
              </h4>
              <small className="text-muted">
                Precios actuales registrados de tus rivales asignados a esta estación
              </small>
            </div>
            {loadingRivals && (
              <span className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </span>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={6}>
              {renderRivalsCard(
                "Regular",
                active?.magna?.suggestedPrice,
                rivalPriceStats.magna,
                "success",
              )}
            </Col>
            <Col lg={6}>
              {renderRivalsCard(
                "Premium",
                active?.premium?.suggestedPrice,
                rivalPriceStats.premium,
                "danger",
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <ToastContainer position="bottom-right" />
    </Fragment>
  );
};

export default SalesForecast;
